import type { FindingsPeriodMode, FindingsScope } from "@iamu/shared";
import {
  CalendarRange,
  ChevronDown,
  GitCommitHorizontal,
  RotateCcw,
  X,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FilterBar, FilterPill } from "@/components/ui/filter-bar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { MobileScope } from "./mobile-scope";
import { PeriodOptions } from "./period-options";
import {
  ALL,
  COMMITTED_HELP,
  facetOptions,
  LENS_OPTIONS,
  type LensValue,
  MODE_HELP,
  SEVERITY_OPTIONS,
  type SeverityValue,
  type ScopeProps,
  useFindingsScope,
  useScopeBuilds,
  useScopeEntities,
  withPeriod,
} from "./scope-hooks";
import { environmentLabel, isScoped, periodLabel } from "./scope-url-state";

export { useFindingsScope, useScopeBuilds };

export function FindingsScopeBar({ projectId }: { projectId: string }) {
  const { scope, setScope } = useFindingsScope();
  return (
    <section aria-label="Findings scope">
      <DesktopScopeBar
        projectId={projectId}
        scope={scope}
        setScope={setScope}
      />
      <MobileScope projectId={projectId} scope={scope} setScope={setScope} />
    </section>
  );
}

function DesktopScopeBar({ projectId, scope, setScope }: ScopeProps) {
  const { personas, capabilities } = useScopeEntities(projectId);
  const { environments, branches, hasUncommitted } = useScopeBuilds(
    projectId,
    scope.period,
  );
  const set = (patch: Partial<FindingsScope>) =>
    setScope({ ...scope, ...patch });

  const pill = (
    label: string,
    value: string | undefined,
    allLabel: string,
    options: ReadonlyArray<{ value: string; label: string }>,
    onChange: (v: string | undefined) => void,
  ) => (
    <FilterPill
      ariaLabel={`Filter by ${label}`}
      value={value ?? ALL}
      maxWidth="max-w-[220px]"
      onValueChange={(v) => onChange(v === ALL ? undefined : v)}
      onRemove={value ? () => onChange(undefined) : undefined}
      options={[{ value: ALL, label: allLabel }, ...options]}
    />
  );

  return (
    <FilterBar className="hidden md:flex">
      <PeriodPill projectId={projectId} scope={scope} setScope={setScope} />
      {scope.period && (
        <ModeToggle
          value={scope.periodMode ?? "seen"}
          onChange={(m) => set({ periodMode: m === "new" ? "new" : undefined })}
        />
      )}
      {personas.length > 0 &&
        pill(
          "persona",
          scope.personaId,
          "All personas",
          personas.map((p) => ({ value: p.id, label: p.name })),
          (v) => set({ personaId: v }),
        )}
      {capabilities.length > 0 &&
        pill(
          "capability",
          scope.capabilityId,
          "All capabilities",
          capabilities.map((c) => ({ value: c.id, label: c.name })),
          (v) =>
            set({
              capabilityId: v,
              capabilityScope: v ? scope.capabilityScope : undefined,
            }),
        )}
      {pill(
        "severity",
        scope.severities?.[0],
        "Any severity",
        SEVERITY_OPTIONS,
        (v) => set({ severities: v ? [v as SeverityValue] : undefined }),
      )}
      {pill("lens", scope.lens, "Every lens", LENS_OPTIONS, (v) =>
        set({ lens: v as LensValue | undefined }),
      )}
      {(environments.length > 1 || scope.environment) &&
        pill(
          "environment",
          scope.environment,
          "All environments",
          facetOptions(environments, scope.environment, environmentLabel),
          (v) => set({ environment: v }),
        )}
      {(branches.length > 1 || scope.branch) &&
        pill(
          "branch",
          scope.branch,
          "All branches",
          facetOptions(branches, scope.branch),
          (v) => set({ branch: v }),
        )}
      {(hasUncommitted || scope.committedOnly) && (
        <SimpleTooltip content={COMMITTED_HELP}>
          <Button
            variant={scope.committedOnly ? "secondary" : "ghost"}
            size="sm"
            aria-pressed={Boolean(scope.committedOnly)}
            onClick={() =>
              set({ committedOnly: scope.committedOnly ? undefined : true })
            }
            className="gap-1.5"
          >
            <GitCommitHorizontal className="size-3.5" aria-hidden="true" />
            Hide uncommitted
          </Button>
        </SimpleTooltip>
      )}
      {isScoped(scope) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setScope({})}
          className="gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <RotateCcw className="size-3" aria-hidden="true" />
          Reset scope
        </Button>
      )}
    </FilterBar>
  );
}

const PILL =
  "inline-flex h-9 items-center rounded-full border border-input bg-background text-sm shadow-xs transition-colors hover:border-foreground/40";

function PeriodPill({ projectId, scope, setScope }: ScopeProps) {
  const [open, setOpen] = useState(false);
  const { builds, selectedLabel } = useScopeBuilds(projectId, scope.period);
  const label = periodLabel(scope.period, selectedLabel);
  const active = Boolean(scope.period);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn(PILL, active && "border-foreground")}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Period: ${label}`}
            className={cn(
              "inline-flex h-full items-center gap-1.5 pl-3 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring",
              active ? "rounded-l-full pr-2 font-medium" : "rounded-full pr-3",
            )}
          >
            <CalendarRange className="size-3.5" aria-hidden="true" />
            {label}
            <ChevronDown
              className="size-3.5 text-muted-foreground"
              aria-hidden="true"
            />
          </button>
        </PopoverTrigger>
        {active && (
          <>
            <div className="h-4 w-px shrink-0 bg-border" aria-hidden="true" />
            <SimpleTooltip content="Show all time">
              <button
                type="button"
                onClick={() => setScope(withPeriod(scope, undefined))}
                aria-label="Remove period (show all time)"
                className="flex h-full w-8 shrink-0 items-center justify-center rounded-r-full text-muted-foreground hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            </SimpleTooltip>
          </>
        )}
      </div>
      <PopoverContent className="w-[23rem] p-0">
        <PeriodOptions
          period={scope.period}
          builds={builds}
          onChange={(p) => {
            setScope(withPeriod(scope, p));
            if (p?.kind !== "range") setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export function ModeToggle({
  value,
  onChange,
  className,
}: {
  value: FindingsPeriodMode;
  onChange: (m: FindingsPeriodMode) => void;
  className?: string;
}) {
  return (
    <fieldset className={cn("inline-flex items-center gap-1", className)}>
      <legend className="sr-only">Which findings in the period</legend>
      {(
        [
          ["seen", "Still happening"],
          ["new", "New in period"],
        ] as const
      ).map(([mode, text]) => (
        <SimpleTooltip key={mode} content={MODE_HELP}>
          <Button
            variant={value === mode ? "secondary" : "ghost"}
            size="sm"
            aria-pressed={value === mode}
            onClick={() => onChange(mode)}
          >
            {text}
          </Button>
        </SimpleTooltip>
      ))}
    </fieldset>
  );
}
